import type { TWRChart, BenchmarkPoint, AssetChart } from "@/schemas";

interface PricePoint {
  timestamp: string;
  price: number;
}

interface PricePointWithTime extends PricePoint {
  timestampMs: number;
}

/**
 * Get interpolated price at any given timestamp from sorted price points.
 */
export function getInterpolatedPrice(
  sortedPoints: PricePointWithTime[],
  targetTime: number
): number | null {
  if (sortedPoints.length === 0) return null;

  const firstPoint = sortedPoints[0];
  const lastPoint = sortedPoints[sortedPoints.length - 1];

  if (targetTime <= firstPoint.timestampMs) return firstPoint.price;
  if (targetTime >= lastPoint.timestampMs) return lastPoint.price;

  // Binary search to find the right interval.
  let left = 0;
  let right = sortedPoints.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midTime = sortedPoints[mid].timestampMs;

    if (midTime === targetTime) {
      return sortedPoints[mid].price;
    }

    if (midTime < targetTime) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  const lower = sortedPoints[Math.max(0, right)];
  const upper = sortedPoints[Math.min(sortedPoints.length - 1, left)];

  if (upper.timestampMs === lower.timestampMs) {
    return lower.price;
  }

  const ratio = (targetTime - lower.timestampMs) / (upper.timestampMs - lower.timestampMs);
  return lower.price + (upper.price - lower.price) * ratio;
}

/**
 * Align benchmark data to TWR timestamps using linear interpolation.
 * Returns benchmark points aligned to the same timestamps as TWR data
 * with returns calculated relative to the TWR start date.
 */
export function alignBenchmarkToTWR(
  chart: AssetChart | null,
  displayTwrChartData: TWRChart | null
): BenchmarkPoint[] | null {
  if (!chart || !chart.price_points || chart.price_points.length === 0) {
    return null;
  }

  if (!displayTwrChartData || !displayTwrChartData.twr_points?.length) {
    return null;
  }

  // Sort benchmark points by timestamp
  const sortedBenchmarkPoints: PricePointWithTime[] = chart.price_points
    .map((point) => ({
      ...point,
      timestampMs: new Date(point.timestamp).getTime(),
    }))
    .sort((a, b) => a.timestampMs - b.timestampMs);

  // Get the display TWR start date to find the initial benchmark price
  const twrStartDate = new Date(displayTwrChartData.twr_points[0].timestamp).getTime();
  
  // Use interpolation to find the exact price at the start of the TWR period
  const startPrice = getInterpolatedPrice(sortedBenchmarkPoints, twrStartDate);
  
  if (startPrice === null) {
    return null;
  }

  // Align benchmark data to display TWR timestamps using linear interpolation
  const aligned: BenchmarkPoint[] = [];
  let benchmarkIdx = 0;

  for (const twrPoint of displayTwrChartData.twr_points) {
    const twrTime = new Date(twrPoint.timestamp).getTime();
    while (
      benchmarkIdx < sortedBenchmarkPoints.length - 2 &&
      sortedBenchmarkPoints[benchmarkIdx + 1].timestampMs < twrTime
    ) {
      benchmarkIdx += 1;
    }

    const lower = sortedBenchmarkPoints[benchmarkIdx];
    const upper = sortedBenchmarkPoints[Math.min(benchmarkIdx + 1, sortedBenchmarkPoints.length - 1)];

    let interpolatedPrice: number;
    if (twrTime <= sortedBenchmarkPoints[0].timestampMs) {
      interpolatedPrice = sortedBenchmarkPoints[0].price;
    } else if (twrTime >= sortedBenchmarkPoints[sortedBenchmarkPoints.length - 1].timestampMs) {
      interpolatedPrice = sortedBenchmarkPoints[sortedBenchmarkPoints.length - 1].price;
    } else if (upper.timestampMs === lower.timestampMs) {
      interpolatedPrice = lower.price;
    } else {
      const ratio = (twrTime - lower.timestampMs) / (upper.timestampMs - lower.timestampMs);
      interpolatedPrice = lower.price + (upper.price - lower.price) * ratio;
    }

    aligned.push({
      timestamp: twrPoint.timestamp,
      return_pct: (interpolatedPrice - startPrice) / startPrice,
    });
  }
  
  return aligned;
}
